import React from 'react';

import type {
	Extension,
	ExtensionHandler,
} from '@atlaskit/editor-common/extensions/extension-handler';
import type { Parameters as ExtensionParameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import { NativeEmbedInitialPlaceholder } from '@atlaskit/native-embeds-common/initial-placeholder';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

const createNativeEmbedInterimPlaceholder = (extension: ExtensionParameters): React.ReactElement =>
	React.createElement(NativeEmbedInitialPlaceholder, {
		firstPaint: true,
		parameters: extension.parameters,
		testId: 'native-embed-renderer-interim-placeholder',
	});

export function createExtensionHandlersWithoutSSR(
	delegates: ADFRendererProps['extensionHandlers'],
): ADFRendererProps['extensionHandlers'] {
	const extensionType = EXTENSION_TYPE.MACRO_CORE;
	const delegate = getExtensionRenderer(delegates?.[extensionType]);

	if (delegate) {
		return {
			[extensionType]: function extensionHandler(ext) {
				if (
					ext.extensionType === extensionType &&
					ext.extensionKey?.startsWith(EXTENSION_KEY.NATIVE_EMBED) &&
					fg('native_embeds_first_paint_placeholder')
				) {
					return createNativeEmbedInterimPlaceholder(ext);
				}

				if (
					(ext.extensionKey === EXTENSION_KEY.TOC ||
						// Calendar does not need to check migration flag since it allows the delegate
						// in renderer-macro-extension-handler to decide between AFM vs legacy
						ext.extensionKey === EXTENSION_KEY.TEAM_CALENDARS ||
						(ext.extensionKey === EXTENSION_KEY.HIDE &&
							expVal('platform_editor_hide_extension_renderer_support', 'isEnabled', false)) ||
						(ext.extensionKey?.startsWith(EXTENSION_KEY.NATIVE_EMBED) &&
							(expVal('cc-maui-experiment', 'isEnabled', false) ||
								fg('platform_native_embeds_rollout_non_maui_experience')))) &&
					ext.extensionType === extensionType
				) {
					return delegate.apply(this, arguments as unknown as Parameters<ExtensionHandler>);
				} else {
					return null;
				}
			},
		};
	}
}

function getExtensionRenderer<T extends ExtensionParameters>(
	extensionHandler: Extension<T> | ExtensionHandler<T> | undefined,
): ExtensionHandler<T> | undefined {
	if (extensionHandler && typeof extensionHandler === 'object') {
		return extensionHandler.render;
	} else {
		return extensionHandler;
	}
}
